import { IItemCache } from '../IItemCacheObserver';
import { IOperationPayload, getOperationTypeKey } from '../../store/actions/implementOperation';
import { getItemsOperation } from '../../operations/getItemsOperation';
import { resolvedFacet, DemandResolution } from '../../facets/DemandFacets';
import {
  preferredKeyFacetFacet,
  supportedFacetsItemKeyFacet,
  supportedFacetsFacet,
  isOperationHandlerRegisteredFacet
} from '../../facets/OperationFacets';
import { KeyFacet } from '../KeyFacet';
import { subtract } from '../../utilities/Graft';
import { pickObject, mapObject } from '../../utilities/objects';
import { IFaceted, pack } from '../Facet';

export function excludeResolvedDemands(
  itemCache: IItemCache,
  payload: IOperationPayload<typeof getItemsOperation.type>
): IOperationPayload<typeof getItemsOperation.type> {
  const {
    items
  } = payload;

  const remainingItems: typeof items = {};

  for (const itemKey in items) {
    if (Object.prototype.hasOwnProperty.call(items, itemKey)) {
      const resolved = itemCache.demandItemFacet(resolvedFacet, itemKey);

      const keyFacet = itemCache.demandItemFacet(preferredKeyFacetFacet, itemKey) || KeyFacet.get(itemKey);

      const supportedFacetsItemKey = supportedFacetsItemKeyFacet.serialize({
        keyFacetId: keyFacet.id
      });

      const operationTypeKey = getOperationTypeKey({ operationTypeFacet: getItemsOperation, keyFacet: keyFacet });

      const supportedFacets = itemCache.demandItemFacet(supportedFacetsFacet, supportedFacetsItemKey);

      const supportedDemands = supportedFacets && supportedFacets.demands;

      const {
        demands
      } = items[itemKey];

      const allowedDemands = subtract(
        supportedDemands ? pickObject(demands, supportedDemands) as IFaceted<boolean> : demands,
        {
          ...pack<{}, boolean>({}, {}),
          // resolvedFacet and preferredKeyFacetFacet are 'meta' facets and should never be fetched.
          [resolvedFacet.id]: true,
          [preferredKeyFacetFacet.id]: true
        }
      );

      const resolvedDemands = resolved && mapObject(
        resolved.demands,
        () => true,
        (key: keyof IFaceted<DemandResolution>, value: DemandResolution): keyof IFaceted<boolean> | undefined => {
          if (value === DemandResolution.unhandled) {
            if (itemCache.demandItemFacet(isOperationHandlerRegisteredFacet, operationTypeKey)) {
              return undefined;
            }
          }

          return key;
        }) as IFaceted<boolean>;

      let remainingDemands: IFaceted<boolean> | undefined;

      if (Object.keys(allowedDemands).length > 0 &&
        (!resolvedDemands || (
          (remainingDemands = subtract(
            allowedDemands,
            resolvedDemands
          )) &&
          remainingDemands && Object.keys(remainingDemands).length > 0
        ))) {
        // If the item is not present in the cache, or does not have a resolved facet,
        // or if there are demands remaining after excluding those already resolved,
        // include the remaining demands, falling back to the original demands.
        remainingItems[itemKey] = {
          demands: remainingDemands || allowedDemands
        };
      } else {
        // The demands are already resolved by the current item in the cache.
      }
    }
  }

  return {
    items: remainingItems
  };
}
